<template>
  <!--
    ── El borrador antes de hacerlo ─────────────────────────────────────────
    La misma tarjeta en el panel y en la llamada, sobre el mismo estado
    (store.pendingAction). Lo que el módulo marca editable se corrige aquí sin
    otra vuelta de conversación: el servidor lo guarda en la propuesta Y en la
    conversación, y devuelve la tarjeta redibujada. Los avisos dicen qué falta;
    ninguno bloquea, porque una orden sin día es un borrador legítimo.
  -->
  <section class="proposal" :class="{ 'proposal--compact': compact }" :aria-busy="store.revising || store.sending">
    <header class="proposal__head">
      <span class="proposal__icon" aria-hidden="true">{{ icon }}</span>
      <span class="proposal__action">{{ say(action.presentation?.action) || say(action.phrase) || toolLabel(action, { t, te }) }}</span>
    </header>

    <!-- Pasada a mano: demasiadas vueltas para hablarla. Lo entendido viaja. -->
    <div v-if="action.handoff && manualRoute" class="proposal__handoff" role="status">
      <p>{{ t('assistant.card.handoff') }}</p>
      <RouterLink :to="manualRoute" class="proposal__handoff-link">{{ t('assistant.card.manual') }} →</RouterLink>
    </div>

    <dl v-if="fields.length" class="proposal__fields">
      <div
        v-for="field in fields" :key="field.key"
        class="row-field"
        :class="{ 'row-field--editing': editing === field.key, 'row-field--doubt': field.doubt }"
      >
        <dt>
          {{ say(field.label) }}
          <span v-if="field.doubt" class="row-field__doubt">{{ t('assistant.card.doubt') }}</span>
        </dt>
        <dd>
          <!-- Leyendo -->
          <template v-if="editing !== field.key">
            <span class="row-field__value" :class="{ 'row-field__value--empty': field.empty }">{{ shown(field) }}</span>
            <button
              v-if="field.edit"
              type="button"
              class="row-field__edit"
              :disabled="store.revising || store.sending"
              :aria-label="`${field.doubt ? t('assistant.card.review') : t('assistant.card.change')} ${say(field.label)}`"
              @click="startEdit(field)"
            >{{ field.doubt ? t('assistant.card.review') : t('assistant.card.change') }}</button>
          </template>

          <!-- Corrigiendo -->
          <form v-else class="row-field__form" @submit.prevent="saveEdit(field)">
            <UiInput
              v-if="field.edit.type === 'text'"
              v-model="draft"
              :maxlength="field.edit.maxLength || 500"
              autofocus
            />
            <template v-else-if="field.edit.type === 'date'">
              <div class="chips">
                <button v-for="shortcut in dateShortcuts" :key="shortcut.value" type="button" class="chip" :class="{ 'chip--on': draft === shortcut.value }" @click="draft = shortcut.value">{{ shortcut.label }}</button>
              </div>
              <input v-model="draft" type="date" class="native-date">
            </template>
            <UiInput
              v-else-if="field.edit.type === 'number' || field.edit.type === 'integer'"
              v-model="draft"
              type="number"
            />
            <div v-else-if="field.edit.type === 'choice'" class="chips" role="radiogroup" :aria-label="say(field.label)">
              <button
                v-for="option in field.edit.options" :key="option.value"
                type="button" role="radio"
                class="chip" :class="{ 'chip--on': draft === option.value }"
                :aria-checked="draft === option.value"
                @click="draft = option.value"
              >{{ say(option.label) }}</button>
            </div>
            <template v-else-if="field.edit.type === 'reference'">
              <div v-if="field.edit.multiple && picks.length" class="chips">
                <span v-for="(pick, index) in picks" :key="pick.id" class="chip chip--on">
                  {{ pick.name }}
                  <button type="button" class="chip__x" :aria-label="t('common.remove')" @click="picks.splice(index, 1)">×</button>
                </span>
              </div>
              <UiReferenceField
                v-model="searchName"
                v-model:reference-id="searchId"
                :type="field.edit.refType"
                :placeholder="t('assistant.card.search')"
                @picked="entry => onPicked(field, entry)"
              />
            </template>

            <p v-if="fieldError" class="row-field__error" role="alert">{{ fieldError }}</p>

            <div class="row-field__buttons">
              <UiButton size="sm" type="submit" :loading="store.revising">{{ t('common.save') }}</UiButton>
              <UiButton size="sm" variant="ghost" type="button" @click="cancelEdit">{{ t('common.cancel') }}</UiButton>
              <UiButton
                v-if="field.edit.type === 'date' || (field.edit.type === 'reference' && !field.edit.multiple)"
                size="sm" variant="ghost" type="button"
                @click="clearField(field)"
              >{{ t('assistant.card.clear') }}</UiButton>
            </div>
          </form>
        </dd>
      </div>
    </dl>
    <p v-else-if="!rows.length" class="proposal__summary">{{ say(action.phrase) || action.summary }}</p>

    <!-- ── Varias escrituras: una fila por cada una, en el orden en que se
         harán. «Hacer» solo en la siguiente que espera (van en orden), y
         «Quitar» mientras quede más de una: quitar la última es cancelar. -->
    <ol v-if="rows.length" class="proposal__rows">
      <li v-for="row in rows" :key="row.index" class="row-write" :class="`row-write--${row.status}`">
        <div class="row-write__head">
          <span class="row-write__icon" aria-hidden="true">{{ ICONS[row.presentation?.icon] ?? '•' }}</span>
          <span class="row-write__action">{{ say(row.presentation?.action) || say(row.phrase) || toolLabel(row, { t, te }) }}</span>
          <span v-if="row.status !== 'pending'" class="row-write__status">{{ t(`assistant.card.status.${row.status}`) }}</span>
        </div>
        <p v-if="row.presentation?.action && say(row.phrase)" class="row-write__summary">{{ say(row.phrase) }}</p>
        <dl v-if="!compact && row.presentation?.fields?.length" class="row-write__fields">
          <div v-for="(field, index) in row.presentation.fields" :key="index">
            <dt>{{ say(field.label) }}</dt>
            <dd>{{ shown(field) }}</dd>
          </div>
        </dl>
        <div v-if="!compact && row.status === 'pending'" class="row-write__buttons">
          <button
            v-if="row.index === nextIndex"
            type="button" class="row-write__do"
            :disabled="store.revising || store.sending"
            @click="$emit('confirm', row.index)"
          >{{ t('assistant.card.doThis') }}</button>
          <button
            v-if="waiting > 1"
            type="button" class="row-write__drop"
            :disabled="store.revising || store.sending"
            @click="store.dropItem(row.index)"
          >{{ t('assistant.card.removeRow') }}</button>
        </div>
      </li>
    </ol>

    <!-- Lo que no se confirma con un «sí»: permisos, pagos, envíos a terceros. -->
    <p v-if="highRisk" class="proposal__risk" role="note">{{ t('assistant.card.highRisk') }}</p>

    <ul v-if="notes.length" class="proposal__notes">
      <li v-for="(note, index) in notes" :key="index">{{ note }}</li>
    </ul>

    <!-- Lo adjuntado en el chat, que se colgará del registro al confirmar. -->
    <p v-if="action.attachments?.length" class="proposal__attached">
      📎 {{ t('assistant.attach.willAttach', { files: action.attachments.map(file => file.name).join(', ') }) }}
    </p>

    <RouterLink v-if="manualRoute && !action.handoff" :to="manualRoute" class="proposal__manual">
      {{ t('assistant.card.manual') }}
    </RouterLink>

    <div class="proposal__buttons">
      <UiButton :loading="store.sending" :disabled="store.revising || editing !== null" @click="$emit('confirm', null)">
        {{ rows.length ? t('assistant.card.doAll', { count: waiting }) : t('assistant.confirmDo') }}
      </UiButton>
      <UiButton variant="secondary" :disabled="store.sending" @click="$emit('reject')">{{ t('common.cancel') }}</UiButton>
    </div>
    <!-- «O di "sí"» no vale para lo delicado: la tarjeta no lo ofrece. -->
    <p v-if="hint && !highRisk" class="proposal__hint">{{ hint }}</p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiInput, UiReferenceField, errorMessage } from '@general/ui-kit'

import { useAssistantStore } from '../store'
import { toolLabel } from '../toolLabel'

const props = defineProps({
  action: { type: Object, required: true },
  /** Lo que se dice debajo de los botones: «o di "sí"» en una llamada. */
  hint: { type: String, default: '' },
  compact: Boolean,
})
defineEmits(['confirm', 'reject'])

const { t, te, locale } = useI18n()
const store = useAssistantStore()

const ICONS = { stock: '📦', task: '🛠', incident: '⚠️', document: '📄', calendar: '📅', money: '€', person: '👤', note: '📝' }
const icon = computed(() => ICONS[props.action.presentation?.icon] ?? '✦')

/** Un Phrase del servidor ({ text, key, params }) en el idioma de quien mira. */
function say(phrase) {
  if (!phrase) return ''
  if (typeof phrase === 'string') return phrase
  return phrase.key && te(phrase.key) ? t(phrase.key, phrase.params ?? {}) : (phrase.text ?? '')
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

const fields = computed(() => (props.action.presentation?.fields ?? []).map((field, index) => ({
  ...field,
  key: field.arg || `f${index}`,
  // Un valor que es la frase «sin día» / «nadie todavía» se lee como vacío.
  empty: Boolean(field.value?.key) && field.edit && field.edit.type !== 'text',
})))

const notes = computed(() => (props.action.presentation?.notes ?? []).map(say).filter(Boolean))

// ── Una lista de escrituras (`action.items`) ───────────────────────────────
const rows = computed(() => props.action.items ?? [])
const waiting = computed(() => rows.value.filter(row => row.status === 'pending').length)
/** The next one waiting: they run in order, so only it can be done alone. */
const nextIndex = computed(() => rows.value.find(row => row.status === 'pending')?.index ?? -1)
/** Only the button confirms it (the server refuses a «sí»). */
const highRisk = computed(() => props.action.risk === 'high')

/** El planificador con lo entendido ya escrito (presentation.manual). */
const manualRoute = computed(() => props.action.presentation?.manual?.route ?? null)

function shown(field) {
  const text = say(field.value)
  if (field.edit?.type === 'date' && ISO_DAY.test(text)) {
    // Al mediodía para que ningún huso horario lo pase al día anterior.
    const day = new Date(`${text}T12:00:00`).toLocaleDateString(locale.value, { weekday: 'long', day: 'numeric', month: 'long' })
    return field.detail ? `${day}, ${field.detail}` : day
  }
  return text || '—'
}

// ── Corregir un campo ──────────────────────────────────────────────────────
const editing = ref(null)
const draft = ref('')
const picks = ref([])
const searchName = ref('')
const searchId = ref(null)
const localError = ref('')

const fieldError = computed(() => localError.value || (store.reviseError ? errorMessage(store.reviseError, t) : ''))

const iso = (date) => date.toISOString().slice(0, 10)
const dateShortcuts = computed(() => {
  const day = (offset) => { const date = new Date(); date.setDate(date.getDate() + offset); return iso(date) }
  return [
    { value: day(0), label: t('assistant.card.today') },
    { value: day(1), label: t('assistant.card.tomorrow') },
    { value: day(2), label: t('assistant.card.afterTomorrow') },
  ]
})

function startEdit(field) {
  editing.value = field.key
  localError.value = ''
  store.reviseError = null
  const input = props.action.input ?? {}
  const current = input[field.arg]
  searchName.value = ''
  searchId.value = null
  if (field.edit.type === 'reference') {
    picks.value = field.edit.multiple && Array.isArray(current) ? current.map(entry => ({ id: entry.id, name: entry.name })) : []
    if (!field.edit.multiple && current) {
      searchId.value = String(current)
      searchName.value = field.edit.nameArg ? String(input[field.edit.nameArg] ?? '') : ''
    }
    // Lo que se dijo, ya escrito en el buscador: la duda se resuelve con un
    // toque sobre lo que salga, no repitiendo la frase.
    if (field.doubt && field.search) {
      searchId.value = null
      searchName.value = field.search
    }
  } else if (current === undefined && field.value && !field.value.key) {
    // Un campo de la tarjeta que no es un campo de la orden (las tareas):
    // se corrige partiendo de lo que enseña.
    draft.value = field.value.text ?? ''
  } else {
    draft.value = current === undefined || current === null ? '' : String(current)
    // A day with a time is shown to the date input as the day alone.
    if (field.edit.type === 'date') draft.value = draft.value.slice(0, 10)
  }
}

function cancelEdit() {
  editing.value = null
  localError.value = ''
}

function onPicked(field, entry) {
  if (!entry) return
  if (field.edit.multiple) {
    if (!picks.value.some(pick => pick.id === entry.id)) picks.value.push({ id: entry.id, name: entry.label })
    // Limpio para buscar al siguiente: así se arma una cuadrilla de corrido.
    searchName.value = ''
    searchId.value = null
  }
}

function valueFor(field) {
  if (field.edit.type === 'reference') {
    if (field.edit.multiple) return picks.value
    return searchId.value ? { id: searchId.value, name: searchName.value } : null
  }
  return draft.value
}

async function saveEdit(field) {
  const value = valueFor(field)
  if (field.edit.type === 'reference' && !field.edit.multiple && !value) {
    // Escrito sin elegir de la lista: no es un registro, y no se inventa.
    localError.value = t('assistant.card.pickFromList')
    return
  }
  if (await store.revisePending({ [field.arg]: value })) editing.value = null
}

async function clearField(field) {
  if (await store.revisePending({ [field.arg]: field.edit.type === 'date' ? '' : null })) editing.value = null
}
</script>

<style scoped>
.proposal {
  border: 1px solid var(--color-primary);
  background: var(--color-surface);
  border-radius: var(--radius);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.proposal__head { display: flex; align-items: center; gap: var(--space-2); font-weight: 600; }
.proposal__icon { font-size: 1.1em; }
.proposal__summary { margin: 0; }

.proposal__fields { margin: 0; display: flex; flex-direction: column; }
.row-field {
  display: grid;
  grid-template-columns: minmax(84px, 32%) 1fr;
  gap: var(--space-2);
  padding: 6px 0;
  border-top: 1px solid var(--color-border);
}
.row-field:first-child { border-top: 0; }
.row-field--editing { grid-template-columns: 1fr; }
.row-field dt { color: var(--color-text-mute); font-size: var(--text-sm); padding-top: 2px; overflow-wrap: anywhere; }
.row-field dd { margin: 0; display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-2); min-width: 0; }
.row-field--editing dd { display: block; }
.row-field__value { overflow-wrap: anywhere; }
.row-field__value--empty { color: var(--color-text-mute); font-style: italic; }
.row-field__edit {
  flex: none;
  background: none;
  border: 0;
  padding: 2px 0;
  color: var(--color-primary);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}
.row-field__edit:disabled { opacity: 0.5; cursor: default; }
.row-field__form { display: flex; flex-direction: column; gap: var(--space-2); }
.row-field__buttons { display: flex; flex-wrap: wrap; gap: var(--space-2); }
.row-field__error { margin: 0; color: var(--color-danger); font-size: var(--text-sm); }

.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}
.chip--on { border-color: var(--color-primary); background: var(--color-primary-soft); color: var(--color-primary); }
.chip__x { background: none; border: 0; color: inherit; cursor: pointer; font: inherit; padding: 0 0 0 2px; }
.native-date {
  font: inherit;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
}

.proposal__notes {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  list-style: none;
  border-radius: var(--radius-sm);
  background: var(--color-warning-soft, var(--color-gray-100));
  font-size: var(--text-sm);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.proposal__notes li::before { content: '• '; }
.proposal__attached { margin: 0; font-size: var(--text-sm); color: var(--color-text-soft); overflow-wrap: anywhere; }

.proposal__buttons { display: flex; gap: var(--space-2); }
.proposal__buttons > * { flex: 1; }
.proposal__hint { margin: 0; text-align: center; font-size: var(--text-xs); color: var(--color-text-mute); }
.row-field--doubt {
  background: var(--color-warning-soft, var(--color-gray-100));
  margin: 0 calc(-1 * var(--space-2));
  padding-left: var(--space-2);
  padding-right: var(--space-2);
  border-radius: var(--radius-sm);
}
.row-field__doubt {
  display: inline-block;
  margin-left: 4px;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-warning, var(--color-danger));
}
.row-field--doubt .row-field__edit { font-weight: 600; }

.proposal__rows { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; }
.row-write { padding: 6px 0; border-top: 1px solid var(--color-border); display: flex; flex-direction: column; gap: 4px; }
.row-write:first-child { border-top: 0; }
.row-write--done, .row-write--dropped, .row-write--skipped { opacity: 0.65; }
.row-write--failed .row-write__status, .row-write--unknown .row-write__status { color: var(--color-danger); }
.row-write__head { display: flex; align-items: center; gap: var(--space-2); }
.row-write__action { flex: 1; font-weight: 600; overflow-wrap: anywhere; }
.row-write__status { font-size: var(--text-xs); color: var(--color-text-mute); }
.row-write__summary { margin: 0; font-size: var(--text-sm); }
.row-write__fields { margin: 0; display: grid; grid-template-columns: minmax(84px, 32%) 1fr; gap: 2px var(--space-2); font-size: var(--text-sm); }
.row-write__fields > div { display: contents; }
.row-write__fields dt { color: var(--color-text-mute); }
.row-write__fields dd { margin: 0; overflow-wrap: anywhere; }
.row-write__buttons { display: flex; gap: var(--space-3); }
.row-write__do, .row-write__drop {
  background: none;
  border: 0;
  padding: 2px 0;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-primary);
  cursor: pointer;
}
.row-write__do { font-weight: 600; }
.row-write__drop { color: var(--color-text-mute); }
.row-write__do:disabled, .row-write__drop:disabled { opacity: 0.5; cursor: default; }
.proposal__risk {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-danger-soft, var(--color-gray-100));
  color: var(--color-danger);
  font-size: var(--text-sm);
  font-weight: 600;
}

.proposal__manual { align-self: flex-start; font-size: var(--text-sm); color: var(--color-primary); }
.proposal__handoff {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-primary-soft);
}
.proposal__handoff p { margin: 0; font-size: var(--text-sm); }
.proposal__handoff-link { font-weight: 600; color: var(--color-primary); }
</style>
